import React, { useMemo, useState, useEffect } from "react";
import { Nav, Tab } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { isCountedOrder, orderMoneyReceived, orderNetValue } from '../../../../utils/dashboardOrders';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function toLocalDateKey(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateToKey(d) {
  if (!d || !(d instanceof Date)) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Monday of the week containing d (weeks start on Monday). getDay(): 0=Sun, 1=Mon, ... 6=Sat. */
function getWeekMonday(d) {
  const x = new Date(d);
  const day = x.getDay();
  const daysSinceMonday = (day + 6) % 7; // Sun=6, Mon=0, Tue=1, ...
  x.setDate(x.getDate() - daysSinceMonday);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Returns orders that fall within the selected range (week/month/year/all/custom). */
function ordersInRange(orders, range, weekOffset, monthOffset, yearOffset, customStart, customEnd) {
  const now = new Date();

  if (range === 'Custom' && customStart && customEnd) {
    const startKey = dateToKey(customStart);
    const endKey = dateToKey(customEnd);
    if (!startKey || !endKey) return orders;
    const [from, to] = startKey <= endKey ? [startKey, endKey] : [endKey, startKey];
    return orders.filter((o) => {
      const key = toLocalDateKey(o.order_date || o.created_at);
      return key && key >= from && key <= to;
    });
  }

  if (range === 'All') return orders;

  if (range === 'Week') {
    const ref = new Date(now);
    ref.setDate(ref.getDate() - weekOffset * 7);
    const start = getWeekMonday(ref);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const startKey = dateToKey(start);
    const endKey = dateToKey(end);
    return orders.filter((o) => {
      const key = toLocalDateKey(o.order_date || o.created_at);
      return key && key >= startKey && key <= endKey;
    });
  }

  if (range === 'Month') {
    let y = now.getFullYear();
    let m = now.getMonth() + monthOffset;
    while (m > 11) { m -= 12; y += 1; }
    while (m < 0) { m += 12; y -= 1; }
    const monthStart = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const monthEnd = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return orders.filter((o) => {
      const key = toLocalDateKey(o.order_date || o.created_at);
      return key && key >= monthStart && key <= monthEnd;
    });
  }

  if (range === 'Year') {
    const y = now.getFullYear() - yearOffset;
    const startKey = `${y}-01-01`;
    const endKey = `${y}-12-31`;
    return orders.filter((o) => {
      const key = toLocalDateKey(o.order_date || o.created_at);
      return key && key >= startKey && key <= endKey;
    });
  }

  return orders;
}

function orderRevenueExclBtw(order) {
  const items = order.items || [];
  return items.reduce((sum, item) => {
    const lineTotalIncl = parseFloat(item.total || 0) || 0;
    const rate = parseFloat(item.btw_rate || 0) || 0;
    const lineTotalExcl = rate > 0 ? lineTotalIncl / (1 + rate / 100) : lineTotalIncl;
    return sum + lineTotalExcl;
  }, 0);
}

function computeDailyData(orders, weekOffset = 0) {
  const days = [];
  const ordersPerDay = Array(7).fill(0);
  const revenuePerDay = Array(7).fill(0);

  const ref = new Date();
  ref.setDate(ref.getDate() - weekOffset * 7);
  const monday = getWeekMonday(ref);
  const indexByKey = new Map();

  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    const key = dateToKey(d);
    const label = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    days.push({ key, label });
    indexByKey.set(key, i);
  }

  for (const o of orders) {
    const key = toLocalDateKey(o.order_date || o.created_at);
    const idx = indexByKey.get(key);
    if (idx == null) continue;
    ordersPerDay[idx] += 1;
    revenuePerDay[idx] += orderNetValue(o);
  }

  return { days, ordersPerDay, revenuePerDay };
}

function computeMonthDaysData(orders, monthOffset) {
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth() + monthOffset;
  while (m > 11) { m -= 12; y += 1; }
  while (m < 0) { m += 12; y -= 1; }
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const points = [];
  const revenuePerDay = Array(daysInMonth).fill(0);

  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayLabel = String(d).padStart(2, '0');
    points.push({ key, label: `${dayLabel} ${MONTH_LABELS[m]}` });
  }

  for (const o of orders) {
    const key = toLocalDateKey(o.order_date || o.created_at);
    if (!key || key.slice(0, 7) !== `${y}-${String(m + 1).padStart(2, '0')}`) continue;
    const dayIdx = parseInt(key.slice(8, 10), 10) - 1;
    if (dayIdx >= 0 && dayIdx < daysInMonth) {
      revenuePerDay[dayIdx] += orderNetValue(o);
    }
  }

  return points.map((p, i) => ({
    name: p.label,
    revenue: revenuePerDay[i] ?? 0,
  }));
}

function computeYearlyMonthsData(orders, yearOffset) {
  const now = new Date();
  const y = now.getFullYear() - yearOffset;
  const revenuePerMonth = Array(12).fill(0);

  for (const o of orders) {
    const key = toLocalDateKey(o.order_date || o.created_at);
    if (!key || key.slice(0, 4) !== String(y)) continue;
    const monthIdx = parseInt(key.slice(5, 7), 10) - 1;
    if (monthIdx >= 0 && monthIdx < 12) {
      revenuePerMonth[monthIdx] += orderNetValue(o);
    }
  }

  return MONTH_LABELS.map((name, i) => ({
    name,
    revenue: revenuePerMonth[i] ?? 0,
  }));
}

function computeAllTimeData(orders) {
  const byMonth = new Map();

  for (const o of orders) {
    const key = toLocalDateKey(o.order_date || o.created_at);
    if (!key) continue;
    const monthKey = key.slice(0, 7);
    const prev = byMonth.get(monthKey) || 0;
    byMonth.set(monthKey, prev + orderNetValue(o));
  }

  const sorted = [...byMonth.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  return sorted.map(([monthKey, revenue]) => {
    const [y, m] = monthKey.split('-');
    const monthLabel = MONTH_LABELS[parseInt(m, 10) - 1];
    return { name: `${monthLabel} ${y}`, revenue };
  });
}

/** Daily revenue between two dates (inclusive). */
function computeCustomRangeChartData(orders, startDate, endDate) {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start > end) [start, end] = [end, start];
  const startKey = dateToKey(start);
  const endKey = dateToKey(end);
  const days = [];
  const revenueByKey = new Map();
  for (let d = new Date(start.getTime()); d <= end; d.setDate(d.getDate() + 1)) {
    const key = dateToKey(d);
    days.push({ key, label: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) });
    revenueByKey.set(key, 0);
  }
  for (const o of orders) {
    const key = toLocalDateKey(o.order_date || o.created_at);
    if (!key || key < startKey || key > endKey) continue;
    const prev = revenueByKey.get(key) ?? 0;
    revenueByKey.set(key, prev + orderNetValue(o));
  }
  return days.map(({ key, label }) => ({
    name: label,
    revenue: Number(((revenueByKey.get(key) ?? 0).toFixed(2))),
  }));
}

function getDefaultCustomRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { start, end: new Date(now) };
}

const ProjectOverviewChart = (props) => {
  const { orders: allOrders = [], materials = [], recipes = [], products = [], sales } = props;

  // Cancelled orders, test orders and giveaways are not income: they drop out of every chart and number below.
  const orders = useMemo(() => allOrders.filter(isCountedOrder), [allOrders]);

  const [range, setRange] = useState('Week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [yearOffset, setYearOffset] = useState(0);
  const [customStartDate, setCustomStartDate] = useState(() => getDefaultCustomRange().start);
  const [customEndDate, setCustomEndDate] = useState(() => getDefaultCustomRange().end);

  useEffect(() => {
    if (range === 'Custom' && (!customStartDate || !customEndDate)) {
      const { start, end } = getDefaultCustomRange();
      if (!customStartDate) setCustomStartDate(start);
      if (!customEndDate) setCustomEndDate(end);
    }
  }, [range]);

  const filteredOrders = useMemo(
    () => ordersInRange(orders, range, weekOffset, monthOffset, yearOffset, customStartDate, customEndDate),
    [orders, range, weekOffset, monthOffset, yearOffset, customStartDate, customEndDate]
  );

  const { days, ordersPerDay, revenuePerDay } = useMemo(
    () => computeDailyData(orders, weekOffset),
    [orders, weekOffset]
  );

  const chartData = useMemo(() => {
    if (range === 'Week') {
      return days.map((d, i) => ({
        name: d.label,
        revenue: revenuePerDay[i] ?? 0,
      }));
    }
    if (range === 'Month') {
      return computeMonthDaysData(orders, monthOffset);
    }
    if (range === 'Year') {
      return computeYearlyMonthsData(orders, yearOffset);
    }
    if (range === 'All') {
      return computeAllTimeData(orders);
    }
    if (range === 'Custom' && customStartDate && customEndDate) {
      return computeCustomRangeChartData(orders, customStartDate, customEndDate);
    }
    return [];
  }, [range, days, revenuePerDay, monthOffset, yearOffset, orders, customStartDate, customEndDate]);

  const metrics = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const activeOrders = filteredOrders.filter(o => (o.delivery_status || 'preparing') !== 'delivered').length;
    const unitsSold = filteredOrders.reduce((sum, o) => {
      const items = o.items || [];
      return sum + items.reduce((s, it) => s + (parseFloat(it.quantity) || 0), 0);
    }, 0);
    // Payments received minus refunds (a fully paid order counts as fully collected even if amount_paid is stale)
    const moneyReceived = filteredOrders.reduce((s, o) => s + orderMoneyReceived(o), 0);
    const totalOrderValue = filteredOrders.reduce((s, o) => s + orderNetValue(o), 0);
    const outstanding = Math.max(0, totalOrderValue - moneyReceived);
    const cashCollectedPct = totalOrderValue > 0 ? (moneyReceived / totalOrderValue * 100) : 0;
    const totalCost = filteredOrders.reduce((s, o) => {
      const items = o.items || [];
      return s + items.reduce((itemSum, it) => {
        const qty = parseFloat(it.quantity) || 0;
        const costPerUnit = parseFloat(it.cost_per_unit);
        const cost = Number.isFinite(costPerUnit) ? qty * costPerUnit : 0;
        return itemSum + cost;
      }, 0);
    }, 0);
    const totalNetRevenue = filteredOrders.reduce((s, o) => s + orderRevenueExclBtw(o), 0);
    const profitMargin = totalNetRevenue > 0 ? ((totalNetRevenue - totalCost) / totalNetRevenue * 100) : 0;
    const netProfit = totalNetRevenue - totalCost;

    return {
      totalOrders,
      activeOrders,
      unitsSold,
      moneyReceived,
      netProfit,
      outstanding,
      cashCollectedPct,
      profitMargin,
    };
  }, [filteredOrders]);

  const chartHeight = typeof props.height === 'number' && props.height > 0 ? props.height : 280;

  return (
    <>
      <Tab.Container
        activeKey={range}
        onSelect={(k) => {
          const key = k || 'Month';
          setRange(key);
          if (key === 'Week') setWeekOffset(0);
          if (key === 'Month') setMonthOffset(0);
          if (key === 'Year') setYearOffset(0);
        }}
      >
        <div className="card-header border-0 pb-0 flex-wrap">
          <h4 className="heading mb-0">Tabliya Overview</h4>
          <Nav as="ul" className="nav nav-pills mix-chart-tab">
            <Nav.Item as="li" className="nav-item">
              <Nav.Link eventKey="Week">This week</Nav.Link>
            </Nav.Item>
            <Nav.Item as="li" className="nav-item">
              <Nav.Link eventKey="Month">Month</Nav.Link>
            </Nav.Item>
            <Nav.Item as="li" className="nav-item">
              <Nav.Link eventKey="Year">Year</Nav.Link>
            </Nav.Item>
            <Nav.Item as="li" className="nav-item">
              <Nav.Link eventKey="All">All</Nav.Link>
            </Nav.Item>
            <Nav.Item as="li" className="nav-item">
              <Nav.Link eventKey="Custom">Custom</Nav.Link>
            </Nav.Item>
          </Nav>
          {range === 'Custom' && (
            <div className="overview-date-range-filter">
              <span className="overview-date-range-label">
                <i className="fa-solid fa-calendar-days me-1" />
                From
              </span>
              <DatePicker
                selected={customStartDate}
                onChange={(d) => setCustomStartDate(d)}
                selectsStart
                startDate={customStartDate}
                endDate={customEndDate}
                maxDate={customEndDate}
                dateFormat="dd MMM yyyy"
                className="form-control form-control-sm overview-date-input"
                placeholderText="Start date"
              />
              <span className="overview-date-range-sep">→</span>
              <span className="overview-date-range-label">To</span>
              <DatePicker
                selected={customEndDate}
                onChange={(d) => setCustomEndDate(d)}
                selectsEnd
                startDate={customStartDate}
                endDate={customEndDate}
                minDate={customStartDate}
                dateFormat="dd MMM yyyy"
                className="form-control form-control-sm overview-date-input"
                placeholderText="End date"
              />
            </div>
          )}
          <div className="ms-auto d-flex align-items-center gap-2">
            {range === 'Week' && (
              <>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setWeekOffset(weekOffset + 1)}>‹ Prev week</button>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))} disabled={weekOffset === 0}>Next week ›</button>
              </>
            )}
            {range === 'Month' && (
              <>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setMonthOffset(monthOffset + 1)}>‹ Prev month</button>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))} disabled={monthOffset === 0}>Next month ›</button>
              </>
            )}
            {range === 'Year' && (
              <>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setYearOffset(yearOffset + 1)}>‹ Prev year</button>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setYearOffset(Math.max(0, yearOffset - 1))} disabled={yearOffset === 0}>Next year ›</button>
              </>
            )}
          </div>
        </div>
        <div className="card-body p-0">
          <div id="overiewChart" style={{ width: '100%', height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3AC977" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3AC977" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#888' }}
                  minTickGap={20}
                  interval="preserveStartEnd"
                  padding={{ left: 8, right: 8 }}
                />
                <YAxis tick={{ fontSize: 12, fill: '#888' }} />
                <Tooltip
                  formatter={(value) => [`€${Number(value).toFixed(2)}`, 'Revenue']}
                />
                <Legend />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#3AC977" fill="url(#colorRevenue)" strokeWidth={1} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="ttl-project">
            <div className="pr-data">
              <h5>{metrics.totalOrders.toLocaleString()}</h5>
              <span>Total Orders</span>
            </div>
            <div className="pr-data">
              <h5>{metrics.activeOrders.toLocaleString()}</h5>
              <span>Active Orders</span>
            </div>
            <div className="pr-data">
              <h5>{recipes.length}</h5>
              <span>Recipes</span>
            </div>
            <div className="pr-data">
              <h5>{products.length}</h5>
              <span>Products</span>
            </div>
            <div className="pr-data">
              <h5>{Math.round(metrics.unitsSold).toLocaleString()}</h5>
              <span>Units Sold</span>
            </div>
          </div>
          <div className="ttl-project mt-3">
            <div className="pr-data">
              <h5>€{metrics.moneyReceived.toFixed(2)}</h5>
              <span>Money Received</span>
            </div>
            <div className="pr-data">
              <h5>€{metrics.outstanding.toFixed(2)}</h5>
              <span>Outstanding</span>
            </div>
            <div className="pr-data">
              <h5>€{metrics.netProfit.toFixed(2)}</h5>
              <span>Net profit</span>
            </div>
            <div className="pr-data">
              <h5>{metrics.cashCollectedPct.toFixed(1)}%</h5>
              <span>Cash Collected</span>
            </div>
            <div className="pr-data">
              <h5>{metrics.profitMargin.toFixed(1)}%</h5>
              <span>Profit Margin</span>
            </div>
          </div>
        </div>
      </Tab.Container>
    </>
  );
};

export default ProjectOverviewChart;
